/**
 * IndexedDB storage for save games. Two slots live in one object store:
 * `autosave` (overwritten at every day close) and `manual` (the Save Game
 * button). Every call can fail (private windows, blocked storage, quota), so
 * callers treat rejection as "not saved" and say so in the UI.
 */
import type { SaveGameState } from '../../domain/models/contracts';

export type SaveSlot = 'autosave' | 'manual';

export interface StoredSave {
  readonly slot: SaveSlot;
  /** ISO time the snapshot was written from the UI thread. */
  readonly storedAt: string;
  /** Sim hour the snapshot was taken at. */
  readonly hour: number;
  readonly save: SaveGameState;
}

const DB_NAME = 'flightline';
const DB_VERSION = 1;
const STORE_NAME = 'saves';

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'slot' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open the save database'));
  });
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase();
  try {
    const transaction = db.transaction(STORE_NAME, mode);
    const request = run(transaction.objectStore(STORE_NAME));
    const result = await requestToPromise(request);
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Save transaction failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Save transaction aborted'));
    });
    return result;
  } finally {
    db.close();
  }
}

/** Writes a snapshot into a slot, replacing whatever it held. */
export async function writeSlot(slot: SaveSlot, save: SaveGameState): Promise<void> {
  const record: StoredSave = { slot, storedAt: new Date().toISOString(), hour: save.savedAtHour, save };
  await withStore('readwrite', (store) => store.put(record));
}

/** Reads a slot. Resolves to null when nothing has been saved there. */
export async function readSlot(slot: SaveSlot): Promise<StoredSave | null> {
  const record: unknown = await withStore('readonly', (store) => store.get(slot));
  if (record === undefined || record === null) return null;
  return record as StoredSave;
}

/** Triggers a browser download of a snapshot as a .json file. */
export function downloadSave(save: SaveGameState): void {
  const blob = new Blob([JSON.stringify(save)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const day = Math.floor(save.savedAtHour / 24) + 1;
  anchor.href = url;
  anchor.download = `flightline-day-${day}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
